import { Component, input, model, OnChanges, OnDestroy, OnInit, output } from '@angular/core';

import {
  MAT_CHECKBOX_DEFAULT_OPTIONS,
  MatCheckboxDefaultOptions,
} from '@angular/material/checkbox';

import { translate } from '@jsverse/transloco';
import { ReplaySubject } from 'rxjs';

import { SemantriaAccountService } from 'app/providers/semantria-api/semantria-account.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import {
  SemantriaConfigurationDto,
  TaxonomyNodeItem,
} from '../../../../providers/semantria-api/configuration.service';

export interface ConfigurationIncludeData {
  valid: boolean;
  tooltip?: string;
  includes: {
    notes: boolean;
    tags: boolean;
    sentiment_phrases: boolean;
    query_topics: boolean;
    user_entities: boolean;
    taxonomies: boolean;
    concept_topics: boolean;
    blacklist_items: boolean;
  };
}

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-content-form',
  templateUrl: './configuration-content-form.component.html',
  styleUrls: ['./configuration-content-form.component.scss'],
  providers: [
    {
      provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
      useValue: { clickAction: 'noop' } as MatCheckboxDefaultOptions,
    },
  ],
  standalone: false,
})
export class ConfigurationContentFormComponent implements OnInit, OnChanges, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly configurationDto = input<SemantriaConfigurationDto>();
  readonly configurationSettings = input<string[]>();
  readonly configurationTaxonomy = input<Partial<TaxonomyNodeItem>[]>();
  readonly configurationCounts = input<Map<string, number>>();
  readonly accountFeatures = model<string[]>();
  readonly accountLimits = input<any>();

  readonly formChange = output<ConfigurationIncludeData>();
  readonly accountFeatureCheck = output<string[]>();

  hasConfigItems = false;
  includeNotes = true;
  includeTags = true;

  allConfigItems = {
    disable: false,
    include: true,
    indeterminate: false,
    tooltip: undefined,
  };

  configItems = [
    {
      prop: 'sentiment_phrases',
      accountFeature: 'SENTIMENT',
      configSetting: 'SENTIMENT_PHRASES',
      count: 0,
      include: true,
      disable: false,
      errors: [],
      warnings: [],
    },
    {
      prop: 'query_topics',
      targetType: 'QUERY_TOPIC',
      accountFeature: 'QUERY_TOPICS',
      configSetting: 'QUERY_TOPICS',
      count: 0,
      include: true,
      disable: false,
      errors: [],
      warnings: [],
    },
    {
      prop: 'user_entities',
      targetType: 'USER_ENTITY',
      accountFeature: 'ENTITIES',
      configSetting: 'USER_ENTITIES',
      count: 0,
      include: true,
      disable: false,
      errors: [],
      warnings: [],
    },
    {
      prop: 'taxonomies',
      accountFeature: 'TAXONOMY',
      configSetting: 'TAXONOMY',
      count: 0,
      include: true,
      disable: false,
      indeterminate: false,
      tooltip: undefined,
      errors: [],
      warnings: [],
    },
    {
      prop: 'concept_topics',
      targetType: 'CONCEPT_TOPIC',
      accountFeature: 'CONCEPT_TOPICS',
      configSetting: 'CONCEPT_TOPICS',
      count: 0,
      include: true,
      disable: false,
      errors: [],
      warnings: [],
    },
    {
      prop: 'blacklist_items',
      accountFeature: 'BLACKLISTS',
      configSetting: 'BLACKLISTS',
      count: 0,
      include: true,
      disable: false,
      errors: [],
      warnings: [],
    },
  ];
  taxonomyItems = [
    { targetType: 'QUERY_TOPIC', taxonomyIncludes: false },
    { targetType: 'USER_ENTITY', taxonomyIncludes: false },
    { targetType: 'CONCEPT_TOPIC', taxonomyIncludes: false },
  ];

  private unsub$ = new ReplaySubject<void>(1);

  constructor(private accountService: SemantriaAccountService) {}

  ngOnInit(): void {
    if (!this.accountFeatures()) {
      this.accountService.sub.takeUntil(this.unsub$).subscribe((res) => {
        this.accountFeatures.set(res.features);
        this.recalculateConfigItemCheckboxStates();
      });
    }
  }

  ngOnChanges(changes) {
    if (changes.configurationDto?.currentValue) {
      this.configItems.forEach(
        (item) =>
          (item.count = this.configurationDto()[item.prop]
            ? this.configurationDto()[item.prop].length
            : 0)
      );

      this.calculateTaxonomyIncludes();

      this.setCheckboxStates();

      this.recalculateConfigItemCheckboxStates();
    } else if (
      changes.accountLimits?.currentValue ||
      changes.configurationCounts?.currentValue ||
      changes.accountFeatures?.currentValue ||
      changes.configurationSettings?.currentValue
    ) {
      this.recalculateConfigItemCheckboxStates();
    }
  }

  ngOnDestroy(): void {
    this.unsub$.next();
  }

  private calculateTaxonomyIncludes() {
    const hasTargetType = (type, taxonomyNodes: Partial<TaxonomyNodeItem>[]): boolean => {
      if (taxonomyNodes) {
        for (const node of taxonomyNodes) {
          if (node.elements && node.elements.some((e) => e.target_type === type)) {
            return true;
          }

          if (hasTargetType(type, node.children)) {
            return true;
          }
        }
      }

      return false;
    };

    if (this.configurationDto().taxonomies) {
      this.taxonomyItems.forEach(
        (item) =>
          (item.taxonomyIncludes = hasTargetType(
            item.targetType,
            this.configurationDto().taxonomies
          ))
      );
    }
  }

  private setCheckboxStates() {
    this.includeNotes = !!this.configurationDto().notes;
    this.includeTags = !!this.configurationDto().tags?.length;

    this.resetConfigItemCheckboxes();

    this.emitFormChange();
  }

  private resetConfigItemCheckboxes(keepState?) {
    this.configItems.forEach((item) => {
      item.disable = !item.count;
      if (!keepState) {
        item.include = !item.disable;
        item.indeterminate = false;
      }
      item.tooltip = undefined;
      item.errors = [];
      item.warnings = [];
      this.hasConfigItems = this.hasConfigItems || !item.disable;
    });

    this.allConfigItems.disable = !this.hasConfigItems;
    this.allConfigItems.include = this.hasConfigItems;
  }

  private recalculateConfigItemCheckboxStates() {
    if (this.configurationDto()) {
      this.resetConfigItemCheckboxes(true);

      if (this.accountFeatures()) {
        this.checkAccountFeatures();
      }

      if (this.accountLimits()) {
        this.checkCountLimits();
        this.checkTaxonomyElementsLimit();
        this.checkTaxonomyDepthLimit();
      }

      if (this.configurationSettings()) {
        this.checkConfigSettings();
      }

      this.checkTaxonomyDependencies();

      this.hasConfigItems = this.configItems.some((item) => !item.disable);

      this.setAllConfigItemsState();
    }

    this.emitFormChange();
  }

  private checkAccountFeatures() {
    const missingPermissions = this.updateProps(
      (item) => this.hasAccountFeature(item.accountFeature),
      true,
      translate('configurations.content-form.error-account-feature'),
      'ACCOUNT_FEATURE'
    );
    this.accountFeatureCheck.emit(missingPermissions);
  }

  private checkCountLimits() {
    this.updateProps(
      (item) => {
        let ret = item.count <= this.accountLimits()[item.configSetting + '_LIMIT'];
        if (ret && this.configurationCounts()?.size) {
          ret =
            this.configurationCounts().get(item.prop) <=
            this.accountLimits()[item.configSetting + '_LIMIT'];
        }
        return ret;
      },
      true,
      translate('configurations.content-form.error-account-limit'),
      'ACCOUNT_LIMIT'
    );
  }

  private checkTaxonomyElementsLimit() {
    if (!this.configurationTaxonomy()) {
      return;
    }

    const getChildCount = (nodes, size) => {
      for (const node of nodes) {
        if (node.children) {
          size += getChildCount(node.children, 0);
        }

        size += node.elements?.length ?? 0;
      }
      return size;
    };

    const taxonomySize = getChildCount(this.configurationTaxonomy(), 0);

    this.updateProps(
      (item) => {
        if (item.prop !== 'taxonomies') {
          return true;
        }
        return taxonomySize <= this.accountLimits()['TAXONOMY_ELEMENTS_LIMIT'];
      },
      true,
      translate('configurations.content-form.error-taxonomy-element-limit'),
      'TAXONOMY_ELEMENTS_LIMIT'
    );
  }

  private checkTaxonomyDepthLimit() {
    if (!this.configurationTaxonomy()) {
      return;
    }

    const getArrayDepth = (arr) => {
      if (Array.isArray(arr)) {
        // if arr is an array, recurse over it
        return 1 + Math.max(...arr.map(getArrayDepth));
      }
      if (arr.children?.length) {
        // if arr is an object with a children property, recurse over the children
        return 1 + Math.max(...arr.children.map(getArrayDepth));
      }
      return 0;
    };

    this.updateProps(
      (item) => {
        if (item.prop !== 'taxonomies') {
          return true;
        }
        return (
          getArrayDepth(this.configurationTaxonomy()) <=
          this.accountLimits()['TAXONOMY_DEPTH_LIMIT']
        );
      },
      true,
      translate('configurations.content-form.error-taxonomy-depth-limit'),
      'TAXONOMY_DEPTH_LIMIT'
    );
  }

  private checkConfigSettings() {
    this.updateProps(
      (item) => this.hasConfigurationSetting(item.configSetting),
      false,
      translate('configurations.content-form.error-config-support'),
      'ACCOUNT_LIMIT'
    );
  }

  private updateProps(checkFn, disable, tooltip, errorType): string[] {
    const missingPermissions = [];

    this.configItems
      .filter((item) => item.count)
      .forEach((item) => {
        if (!checkFn(item)) {
          item.tooltip = tooltip;
          if (disable) {
            item.disable = true;
            item.include = false;
            item.indeterminate = false;
            item.errors.push(errorType);

            missingPermissions.push(item.accountFeature);
          } else {
            item.warnings.push(errorType);
          }
        }
      });

    return missingPermissions;
  }

  private checkTaxonomyDependencies() {
    const taxonomyItem = this.configItems.find((i) => i.prop === 'taxonomies');

    if (!taxonomyItem.disable) {
      this.configItems
        .filter(
          (item) =>
            item.targetType &&
            this.taxonomyItems.find((ti) => ti.targetType === item.targetType).taxonomyIncludes &&
            !item.include
        )
        .forEach((item) => {
          item.errors.push('TAXONOMY_INCLUDE');

          taxonomyItem.include = false;
          taxonomyItem.indeterminate = true;

          if (item.disable) {
            taxonomyItem.disable = true;
            taxonomyItem.indeterminate = false;
          }

          taxonomyItem.tooltip = this.assembleTaxonomyTooltip();
        });
    }
  }

  toggleAllItems() {
    if (this.allConfigItems.disable) {
      return;
    }

    if (this.allConfigItems.indeterminate) {
      this.allConfigItems.indeterminate = false;
      this.allConfigItems.include = false;
    } else {
      this.allConfigItems.include = !this.allConfigItems.include;
    }

    const taxonomyItem = this.configItems.find((i) => i.prop === 'taxonomies');
    taxonomyItem.indeterminate = false;

    this.configItems
      .filter((ci) => !ci.disable)
      .forEach((ci) => {
        ci.include = this.allConfigItems.include;
        this.handleConfigItemStateChange(ci);
      });

    this.emitFormChange();
  }

  toggleConfigItem(item, emitEvent = true) {
    if (item.disable) {
      return;
    }

    item.include = !item.include;

    this.handleConfigItemStateChange(item);

    if (emitEvent) {
      this.emitFormChange();
    }
  }

  private handleConfigItemStateChange(item) {
    if (item.disable) {
      return;
    }

    const taxonomyItem = this.configItems.find((i) => i.prop === 'taxonomies');
    if (item === taxonomyItem) {
      if (item.indeterminate) {
        item.include = false;
        item.indeterminate = false;
        this.configItems.forEach(
          (ci) => (ci.errors = ci.errors.filter((err) => err !== 'TAXONOMY_INCLUDE'))
        );
      } else if (item.include) {
        this.taxonomyItems
          .filter((ti) => ti.taxonomyIncludes)
          .forEach((ti) => {
            const configItem = this.configItems.find((i) => i.targetType === ti.targetType);
            if (!configItem.disable) {
              configItem.include = true;
            }
          });
      }
    } else if (
      this.taxonomyItems.find((ti) => ti.targetType === item.targetType)?.taxonomyIncludes
    ) {
      if (taxonomyItem.include || taxonomyItem.indeterminate) {
        if (!item.include) {
          item.errors.push('TAXONOMY_INCLUDE');
        } else {
          item.errors = item.errors.filter((err) => err !== 'TAXONOMY_INCLUDE');
        }

        taxonomyItem.indeterminate = this.isTaxonomyIndeterminate();
        taxonomyItem.include = !taxonomyItem.indeterminate;
      }
    }

    this.setAllConfigItemsState();

    taxonomyItem.tooltip = this.assembleTaxonomyTooltip();
  }

  private setAllConfigItemsState() {
    const availableItems = this.configItems.filter((ci) => !ci.disable);
    if (availableItems.length && availableItems.every((ci) => ci.include)) {
      this.allConfigItems.include = true;
      this.allConfigItems.indeterminate = false;
    } else if (availableItems.every((ci) => !ci.include)) {
      this.allConfigItems.include = false;
      this.allConfigItems.indeterminate = false;
    } else {
      this.allConfigItems.indeterminate = true;
    }
  }

  private assembleTaxonomyTooltip() {
    const taxonomyItem = this.configItems.find((i) => i.prop === 'taxonomies');
    const accountFeatureErrors = this.configItems.filter((i) =>
      i.errors.includes('ACCOUNT_FEATURE')
    );
    const accountLimitErrors = this.configItems.filter((i) => i.errors.includes('ACCOUNT_LIMIT'));
    const taxonomyIncludeErrors = this.configItems.filter((i) =>
      i.errors.includes('TAXONOMY_INCLUDE')
    );

    if (taxonomyItem.errors.includes('ACCOUNT_LIMIT')) {
      return translate('configurations.content-form.error-account-limit');
    }

    if (taxonomyItem.warnings.includes('ACCOUNT_LIMIT')) {
      return translate('configurations.content-form.error-config-support');
    }

    if (accountFeatureErrors.length) {
      return translate('configurations.content-form.error-taxonomy-item-permission', {
        items: accountFeatureErrors
          .map((tt) => translate(`configurations.content-form.${tt.prop}`))
          .join(', '),
      });
    }

    if (accountLimitErrors.length) {
      return translate('configurations.content-form.error-taxonomy-item-limit', {
        items: accountFeatureErrors
          .map((tt) => translate(`configurations.content-form.${tt.prop}`))
          .join(', '),
      });
    }

    if (taxonomyIncludeErrors.length) {
      return translate('configurations.content-form.error-taxonomy-item-missing', {
        items: taxonomyIncludeErrors
          .map((tt) => translate(`configurations.content-form.${tt.prop}`))
          .join(', '),
      });
    }
  }

  private isTaxonomyIndeterminate() {
    return this.taxonomyItems.some(
      (ti) =>
        ti.taxonomyIncludes &&
        this.configItems.some((ci) => ti.targetType === ci.targetType && !ci.include)
    );
  }

  toggleNotes() {
    this.includeNotes = !this.includeNotes;
    this.emitFormChange();
  }

  toggleTags() {
    this.includeTags = !this.includeTags;
    this.emitFormChange();
  }

  emitFormChange() {
    this.formChange.emit(this.createFormData());
  }

  private createFormData() {
    const formDataIncludes: any = {
      notes: this.includeNotes,
      tags: this.includeTags,
    };

    this.configItems.forEach((ci) => (formDataIncludes[ci.prop] = ci.include));

    return {
      valid: this.itemsValid,
      tooltip: this.validationTooltip,
      includes: formDataIncludes,
    };
  }

  hasAccountFeature(itemAccountFeature) {
    return !this.accountFeatures() || this.accountFeatures().includes(itemAccountFeature);
  }

  hasConfigurationSetting(itemConfigSetting) {
    return (
      !this.configurationSettings() || this.configurationSettings().includes(itemConfigSetting)
    );
  }

  get itemsValid() {
    return (
      this.configItems.filter((item) => !item.disable).every((item) => !item.errors.length) &&
      (!this.hasConfigItems || this.configItems.some((i) => i.include))
    );
  }

  get validationTooltip() {
    if (this.hasConfigItems && !this.configItems.some((i) => i.include)) {
      return translate('configurations.content-form.error-no-selection');
    }
    const taxonomyItem = this.configItems.find((i) => i.prop === 'taxonomies');
    if (!taxonomyItem.disable) {
      return taxonomyItem.tooltip;
    }
  }
}
